import { ENUM__error_type } from '@shared/asserts/ENUM__error_type';
import { assert_array_includes, assert_array_typeof_string, assert_equal, assert_instanceof, assert_type_string } from '@shared/asserts/utils_asserts';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { MAP__field_data } from '@shared/types/types';
import { UTILS__array } from '@shared/utils/utils_array';
import { ENUM__string_pattern } from '../../../_enums/ENUM__string_pattern';
import { DTO_GRAPH__instance } from '../../../_graph/DTO_GRAPH__instance';
import { ENUM__field_connectable_type } from '../../_base/_methods/ENUM__field_connectable_type';
import { DTO_META__field_info } from '../../_base/DTO_META__field_info';
import { FIELD__base } from '../../_base/FIELD__base';
import { DECORATOR__class_serialization_tested } from '../_base/DECORATOR__class_serialization_tested';
import { DECORATOR__class_serialization_tested_for_hash } from '../_base/DECORATOR__class_serialization_tested_for_hash';
import { DECORATOR__field_is_input } from '../_base/DECORATOR__field_is_input';
import { DECORATOR__field_not_linked } from '../_base/DECORATOR__field_not_linked';
import { FIELD_PRIMITIVE__base } from '../_base/FIELD_PRIMITIVE__base';

@DECORATOR__class_serialization_tested
@DECORATOR__class_serialization_tested_for_hash
export class FIELD_PRIMITIVE__base_string extends FIELD_PRIMITIVE__base {
  /*
   * STATIC FIELDS
   */

  static _class_name_static = ENUM__dto_class_name.GRAPH_FIELD_PRIMITIVE_STRING;

  /*
   * VALUE REGISTER INPUT
   */

  static register_input__value(value: string, options_meta: Partial<DTO_META__field_info> = {}) {
    let out = this.init_as_input(this.create_field_container_default({ value }), options_meta);

    return out;
  }

  /*
   * FIELDS
   */

  allowed_pattern: string[];
  is_empty_allowed: boolean;
  value_empty: string;

  /*
   * PROPERTIES
   */
  get is_empty(): boolean {
    return this.value === this.value_empty;
  }

  field_connectable__type() {
    return ENUM__field_connectable_type.STRING;
  }

  /*
   * VALUE
   */

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  get_value(): string {
    assert_type_string(this.value);

    return this.value;
  }

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  set_value(value: string) {
    if (this.value === value) return;

    this.value = value;
  }

  /*
   * VALUE RANGE
   */
  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  get_value_range(): string[] {
    assert_array_typeof_string(this.value_range);

    return this.value_range;
  }

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  set_value_range(value: unknown) {
    assert_array_typeof_string(value);

    this.value_range = value;
  }

  /*
   * VALUES ALLOWED
   */

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  get_values_allowed(): string[] {
    assert_array_typeof_string(this.values_allowed);

    return this.values_allowed;
  }

  override get grid2d_dimension_values() {
    return [...this.get_value_range()];
  }

  get allowed_pattern_regex() {
    return new RegExp(this.allowed_pattern[0], this.allowed_pattern[1]);
  }
  /*
   * METHODS
   */

  constructor() {
    super();
    this.value_range = [];

    this.allowed_pattern = ENUM__string_pattern.ANY;

    this.is_empty_allowed = true;

    this.values_allowed = [];

    this.value_empty = '';
  }

  add_value_to_values_range(value: string) {
    let values_range = this.get_value_range();

    if (values_range.includes(value)) return;

    /*
     *
     */
    let values_allowed = this.get_values_allowed();

    assert_array_includes(values_allowed, value);

    values_range.push(value);
    values_range = values_range.unique_values();
    values_range.sort((a, b) => values_allowed.indexOf(a) - values_allowed.indexOf(b));

    this.set_value_range(values_range);
  }

  is_field_is_compatible_to_sync_with(other: FIELD__base) {
    if (!super.is_field_is_compatible_to_sync_with(other)) return false;

    assert_instanceof(other, FIELD_PRIMITIVE__base_string);

    if (this.allowed_pattern_regex.source != other.allowed_pattern_regex.source) return false;
    if (this.allowed_pattern_regex.flags != other.allowed_pattern_regex.flags) return false;
    if (this.is_empty_allowed != other.is_empty_allowed) return false;

    let values_allowed_set_this = new Set(this.get_values_allowed());
    let values_allowed_set_other = new Set(other.get_values_allowed());

    let out = values_allowed_set_this.is_equal(values_allowed_set_other);

    return out;
  }

  is_equal(other: FIELD__base) {
    let out = super.is_equal(other);

    if (!out) return false;

    assert_instanceof(other, FIELD_PRIMITIVE__base_string);

    if (this.allowed_pattern_regex.source != other.allowed_pattern_regex.source) return false;
    if (this.allowed_pattern_regex.flags != other.allowed_pattern_regex.flags) return false;
    if (this.is_empty_allowed != other.is_empty_allowed) return false;
    if (this.value_empty != other.value_empty) return false;
    if (this.value != other.value) return false;

    return true;
  }

  /*
   *
   */

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  store__can_deserialize_from_data(map: MAP__field_data) {
    if (!super.store__can_deserialize_from_data(map)) return false;

    /*
     *
     */

    let values_allowed = map.get_or_default('values_allowed', this.values_allowed);
    assert_array_typeof_string(values_allowed);

    if (this.values_allowed.is_empty() && this.is_empty) return true;
    if (UTILS__array.are_both_empty(values_allowed, this.values_allowed)) return true;
    if (!UTILS__array.has_intersection(values_allowed, this.values_allowed)) return false;

    return true;
  }

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  store__serialization_keys_list_black(): string[] {
    let out = super.store__serialization_keys_list_black() as (keyof this)[];

    out.push('is_empty_allowed');
    out.push('value_empty');
    out.push('allowed_pattern');

    return out as string[];
  }

  /*
   * VALIDATION
   */

  _validate__case_base(graph: DTO_GRAPH__instance) {
    super._validate__case_base(graph);

    if (!this.allowed_pattern_regex.test(this.get_value())) this.error_add(ENUM__error_type.VALIDATION_INVALID_VALUE);
    if (!this.is_empty_allowed && this.get_value() == this.value_empty) this.error_add(ENUM__error_type.VALIDATION_INVALID_VALUE);
  }

  assert_standard_fields(): void {
    super.assert_standard_fields();

    assert_array_typeof_string(this.allowed_pattern);
    assert_equal(this.allowed_pattern.length, 2);

    assert_type_string(this.value);
    assert_type_string(this.value_empty);

    assert_array_typeof_string(this.value_range);
  }
}
